import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { currentSeason, recentSeasons } from "../utils/seasons";
import { 

  Trophy, TrendingUp, TrendingDown, Minus, RefreshCw, 
  ChevronDown, Users, Calendar, Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// Always use relative URLs for API calls
const API_URL = "";

// Form indicator component
const FormIndicator = ({ result }) => {
  const colors = {
    W: "bg-green-500",
    D: "bg-gray-400",
    L: "bg-red-500"
  };
  
  return (
    <span 
      className={`inline-block w-5 h-5 rounded-full ${colors[result] || "bg-gray-300"} text-white text-xs flex items-center justify-center font-bold`}
      title={result === "W" ? "Win" : result === "D" ? "Draw" : "Loss"}
    >
      {result}
    </span>
  );
};

// League Table Component
const LeagueTableCard = ({ table }) => {
  if (!table) return null;
  const testId = table.team_type.toLowerCase().replace(/\s+/g, '-');
  const updatedLabel = table.last_updated
    ? new Date(table.last_updated).toLocaleString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;
  if (!table.standings || table.standings.length === 0) {
    return (
      <Card className="overflow-hidden" data-testid={`table-unavailable-${testId}`}>
        <CardHeader className="bg-gradient-to-r from-[#7A263A] to-[#1BB1DC] text-white">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            {table.league_name}
            <span className="text-sm font-normal ml-auto">{table.team_type}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 text-sm text-slate-600">
          Table not available for {table.season} yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-[#7A263A] to-[#1BB1DC] text-white">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          {table.league_name}
          {table.is_live && (
            <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full animate-pulse">
              LIVE
            </span>
          )}
          <span className="text-sm font-normal ml-auto">{table.team_type}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`table-${testId}`}>
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-3 text-left font-semibold">Pos</th>
                <th className="px-2 py-3 text-left font-semibold">Team</th>
                <th className="px-2 py-3 text-center font-semibold">P</th>
                <th className="px-2 py-3 text-center font-semibold">W</th>
                <th className="px-2 py-3 text-center font-semibold">D</th>
                <th className="px-2 py-3 text-center font-semibold">L</th>
                <th className="px-2 py-3 text-center font-semibold">GF</th>
                <th className="px-2 py-3 text-center font-semibold">GA</th>
                <th className="px-2 py-3 text-center font-semibold">GD</th>
                <th className="px-2 py-3 text-center font-semibold">Pts</th>
                <th className="px-2 py-3 text-center font-semibold hidden sm:table-cell">Form</th>
              </tr>
            </thead>
            <tbody>
              {table.standings.map((entry, index) => (
                <tr 
                  key={index}
                  className={`
                    border-b border-slate-100 hover:bg-slate-50 transition
                    ${entry.is_west_ham ? "bg-[#7A263A]/10 font-semibold" : ""}
                    ${entry.position <= 4 ? "border-l-4 border-l-green-500" : ""}
                    ${entry.position >= table.standings.length - 2 ? "border-l-4 border-l-red-500" : ""}
                  `}
                >
                  <td className="px-2 py-3">
                    <span className={`
                      inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                      ${entry.position <= 4 ? "bg-green-500 text-white" : ""}
                      ${entry.position >= table.standings.length - 2 ? "bg-red-500 text-white" : ""}
                      ${entry.position > 4 && entry.position < table.standings.length - 2 ? "bg-slate-200" : ""}
                    `}>
                      {entry.position}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2">
                      {entry.is_west_ham && (
                        <img 
                          src={entry.crest_url || "https://upload.wikimedia.org/wikipedia/en/c/cc/West_Ham_United_FC_logo.svg"}
                          alt="West Ham"
                          className="w-5 h-5"
                        />
                      )}
                      <span className={entry.is_west_ham ? "text-[#7A263A]" : ""}>
                        {entry.team}
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-center">{entry.played}</td>
                  <td className="px-2 py-3 text-center text-green-600">{entry.won}</td>
                  <td className="px-2 py-3 text-center text-gray-500">{entry.drawn}</td>
                  <td className="px-2 py-3 text-center text-red-600">{entry.lost}</td>
                  <td className="px-2 py-3 text-center">{entry.goals_for}</td>
                  <td className="px-2 py-3 text-center">{entry.goals_against}</td>
                  <td className="px-2 py-3 text-center">
                    <span className={`
                      ${entry.goal_difference > 0 ? "text-green-600" : ""}
                      ${entry.goal_difference < 0 ? "text-red-600" : ""}
                    `}>
                      {entry.goal_difference > 0 ? "+" : ""}{entry.goal_difference}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-center font-bold">{entry.points}</td>
                  <td className="px-2 py-3 hidden sm:table-cell">
                    <div className="flex gap-1 justify-center">
                      {entry.form && entry.form.split("").map((result, i) => (
                        <FormIndicator key={i} result={result} />
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Legend */}
        <div className="p-3 bg-slate-50 border-t flex flex-wrap gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 bg-green-500 rounded"></span>
            <span>Champions League / Promotion</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 bg-red-500 rounded"></span>
            <span>Relegation</span>
          </div>
          {updatedLabel && (
            <span className="ml-auto" data-testid={`table-updated-${testId}`}>
              Updated {updatedLabel}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function TablesPage() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [season, setSeason] = useState(currentSeason());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchTables = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/league-tables?season=${season}`);
      if (!response.ok) throw new Error("Failed to fetch tables");
      const data = await response.json();
      setTables(data.tables || []);
      setLastUpdated(new Date().toLocaleTimeString());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchTables, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [season]);

  // Auto-retry when tables empty (uses state counter to guarantee re-renders)
  useEffect(() => {
    if (!loading && tables.length === 0 && !error && retryCount < 8) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        fetchTables();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loading, tables, error, retryCount]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="tables-page">
      <SEO title="League Tables" path="/tables" description="West Ham United league standings. EFL Championship, WSL, Premier League 2, and U18 tables with live positions, points, form and goal difference." />
      <Navbar />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Trophy className="h-8 w-8 text-[#7A263A]" />
              League Tables
            </h1>
            <p className="text-slate-600 mt-1">
              Live standings for all West Ham teams
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={season} onValueChange={setSeason}>
              <SelectTrigger className="w-[140px]" data-testid="season-select">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Season" />
              </SelectTrigger>
              <SelectContent>
                {recentSeasons(4).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <button
              onClick={fetchTables}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#7A263A] text-white rounded-lg hover:bg-[#5a1d2c] transition disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <p className="text-sm text-slate-500 mb-4">
            Last updated: {lastUpdated}
          </p>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && tables.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-[#7A263A]" />
          </div>
        )}

        {/* Tables Empty - Retrying */}
        {!loading && tables.length === 0 && !error && retryCount < 8 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Loader2 className="w-8 h-8 text-[#7A263A] animate-spin mb-4" />
            <p className="text-slate-600 font-medium">Loading league tables...</p>
            <p className="text-slate-400 text-sm mt-1">Table data is being compiled. This page will update automatically.</p>
          </div>
        )}

        {/* Tables Empty - Final */}
        {!loading && tables.length === 0 && (error || retryCount >= 8) && (
          <div className="text-center py-20 text-slate-500">
            <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No league tables available for this season</p>
          </div>
        )}

        <div className="grid gap-8">
          {tables.map((table, index) => (
            <LeagueTableCard key={index} table={table} />
          ))}
        </div>

        {/* Info Card */}
        <Card className="mt-8">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Users className="h-5 w-5 text-[#1BB1DC]" />
              About Our Teams
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-slate-600">
              <div>
                <h4 className="font-medium text-slate-800">Men's First Team</h4>
                <p>Competing in the EFL Championship for the 2026-27 season after relegation from the Premier League. Three-time FA Cup winners.</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-800">Women's First Team</h4>
                <p>Playing in the Women's Super League, developing talent for the future.</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-800">Under-21s</h4>
                <p>Premier League 2 side developing the next generation of Hammers.</p>
              </div>
              <div>
                <h4 className="font-medium text-slate-800">Under-18s</h4>
                <p>Academy team continuing the famous West Ham tradition of youth development.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
